// ResidentFeedbackForm — Resident Portal feedback submission
// Uses submitResidentFeedback (Feedback Module Slice 1 backend callable).
// Security: residentId is derived server-side from claims. locationId is
// required by the backend to load per-location settings.

import { useState } from "react";
import { api } from "../api";
import type { Vehicle } from "./VehicleCard";

interface Props {
  locationId: string;
  vehicles:   Vehicle[];
  onBack:     () => void;
}

type ServiceType = "valet" | "ev" | "car_wash" | "parking" | "building" | "other";

const SERVICE_OPTIONS: { value: ServiceType; label: string }[] = [
  { value: "valet",    label: "Valet"     },
  { value: "ev",       label: "EV Charging" },
  { value: "car_wash", label: "Car Wash"  },
  { value: "parking",  label: "Parking"   },
  { value: "building", label: "Building"  },
  { value: "other",    label: "Other"     },
];

export default function ResidentFeedbackForm({ locationId, vehicles, onBack }: Props) {
  const [serviceType, setServiceType] = useState<ServiceType>("other");
  const [message,     setMessage]     = useState("");
  const [rating,      setRating]      = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [vehicleId,   setVehicleId]   = useState<string>("");
  const [submitting,  setSubmitting]  = useState(false);
  const [done,        setDone]        = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.submitResidentFeedback({
        locationId,
        serviceType,
        message: message.trim(),
        rating:    rating  ?? undefined,
        vehicleId: vehicleId || undefined,
      });
      setDone(true);
    } catch (err: any) {
      const code: string = err?.code ?? "";
      if (code === "functions/failed-precondition" || code.includes("failed-precondition")) {
        setError("Feedback is not available for this location.");
      } else {
        setError(err?.message ?? "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const displayRating = hoverRating ?? rating;

  const cardStyle: React.CSSProperties = {
    background:   "var(--white)",
    borderRadius: "16px",
    border:       "1px solid var(--line)",
    padding:      "28px 24px",
    boxShadow:    "0 1px 2px rgba(11,18,32,0.04)",
  };

  const labelStyle: React.CSSProperties = {
    display:      "block",
    fontSize:     "13px",
    fontWeight:   600,
    color:        "var(--ink)",
    marginBottom: "8px",
  };

  const selectStyle: React.CSSProperties = {
    width:        "100%",
    padding:      "10px 12px",
    borderRadius: "10px",
    border:       "1px solid var(--line)",
    fontSize:     "14px",
    color:        "var(--ink)",
    background:   "var(--white)",
    outline:      "none",
    appearance:   "none",
  };

  const textareaStyle: React.CSSProperties = {
    width:        "100%",
    padding:      "10px 12px",
    borderRadius: "10px",
    border:       "1px solid var(--line)",
    fontSize:     "14px",
    color:        "var(--ink)",
    background:   "var(--white)",
    resize:       "vertical",
    minHeight:    "100px",
    outline:      "none",
    fontFamily:   "inherit",
    boxSizing:    "border-box",
  };

  const fieldStyle: React.CSSProperties = { marginBottom: "20px" };

  // ── Success state ──────────────────────────────────────────────────────────

  if (done) {
    return (
      <div style={{ marginTop: "var(--space-xl)" }}>
        <div style={{ ...cardStyle, textAlign: "center", padding: "48px 24px" }}>
          <div style={{
            width: "52px", height: "52px", borderRadius: "50%",
            background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px",
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: "var(--ink)", margin: "0 0 8px" }}>
            Thank you!
          </h3>
          <p style={{ fontSize: "14px", color: "var(--text-muted)", margin: "0 0 24px", lineHeight: 1.5 }}>
            Your feedback was submitted.
          </p>
          <button
            onClick={onBack}
            style={{
              background: "var(--azzurro-600)", color: "var(--white)",
              border: "none", borderRadius: "10px", padding: "11px 24px",
              fontSize: "14px", fontWeight: 600, cursor: "pointer",
            }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ marginTop: "var(--space-xl)" }}>
      {/* Back + title */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", marginBottom: "var(--space-lg)" }}>
        <button
          onClick={onBack}
          style={{ background: "transparent", border: "none", padding: "8px", cursor: "pointer", color: "var(--stone)" }}
        >
          ← Back
        </button>
        <h2 style={{ fontSize: "20px", fontWeight: 600, color: "var(--ink)", margin: 0 }}>
          Submit Feedback
        </h2>
      </div>

      <div style={cardStyle}>
        <form onSubmit={handleSubmit} noValidate>
          {/* Service type */}
          <div style={fieldStyle}>
            <label htmlFor="fb-service" style={labelStyle}>Service *</label>
            <div style={{ position: "relative" }}>
              <select
                id="fb-service"
                value={serviceType}
                onChange={e => setServiceType(e.target.value as ServiceType)}
                style={selectStyle}
              >
                {SERVICE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
              </span>
            </div>
          </div>

          {/* Rating */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Rating (optional)</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(rating === n ? null : n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(null)}
                  style={{
                    background: "none", border: "none", padding: "2px",
                    cursor: "pointer", fontSize: "28px", lineHeight: 1,
                    color: (displayRating ?? 0) >= n ? "#f59e0b" : "var(--line)",
                    transition: "color 0.1s ease",
                  }}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  ★
                </button>
              ))}
              {rating !== null && (
                <button
                  type="button"
                  onClick={() => setRating(null)}
                  style={{ background: "none", border: "none", fontSize: "12px", color: "var(--text-muted)", cursor: "pointer", padding: "2px 6px", alignSelf: "center" }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Message */}
          <div style={fieldStyle}>
            <label htmlFor="fb-message" style={labelStyle}>Message *</label>
            <textarea
              id="fb-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Tell us about your experience…"
              style={textareaStyle}
              maxLength={2000}
              required
            />
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px", textAlign: "right" }}>
              {message.length}/2000
            </div>
          </div>

          {/* Vehicle (optional, only if vehicles are loaded) */}
          {vehicles.length > 0 && (
            <div style={fieldStyle}>
              <label htmlFor="fb-vehicle" style={labelStyle}>Vehicle (optional)</label>
              <div style={{ position: "relative" }}>
                <select
                  id="fb-vehicle"
                  value={vehicleId}
                  onChange={e => setVehicleId(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">No specific vehicle</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {[v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}{v.plate ? ` · ${v.plate}` : ""}
                    </option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </span>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div role="alert" style={{
              padding: "12px 14px", borderRadius: "10px", marginBottom: "16px",
              background: "rgba(220,38,38,0.07)", color: "var(--danger)", fontSize: "14px",
              border: "1px solid rgba(220,38,38,0.18)", lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!message.trim() || submitting}
            style={{
              width:         "100%",
              padding:       "13px",
              borderRadius:  "12px",
              border:        "none",
              background:    !message.trim() || submitting ? "var(--stone)" : "var(--azzurro-600)",
              color:         "var(--white)",
              fontSize:      "15px",
              fontWeight:    600,
              cursor:        !message.trim() || submitting ? "not-allowed" : "pointer",
              transition:    "background 0.15s ease",
            }}
          >
            {submitting ? "Submitting…" : "Submit Feedback"}
          </button>
        </form>
      </div>
    </div>
  );
}
